import {globalDocument} from '../../../libs/shared/helpers/src/browser/browserHelpers';
import {isSafari} from '../../../libs/shared/helpers/src/environment/platformHelpers';

export const windowListenerInitialValue = 0;

export function shouldDiscardInteraction(
  startTime: number,
  lastVisibilityChange: number,
  lastFocusChange: number,
  animationFrameTime: number,
  nextTaskTime: number,
) {
  // Ignore events while window is hidden.
  if (startTime <= lastVisibilityChange || globalDocument.hidden) {
    return true;
  }

  // Ignore events while window isn't focused.
  if (startTime <= lastFocusChange || !globalDocument.hasFocus()) {
    return true;
  }

  // Ignore certain events on Safari.
  if (shouldDiscardInteractionOnSafari(animationFrameTime, nextTaskTime)) {
    return true;
  }

  return false;
}

/*
 * Helpers.
 */

function shouldDiscardInteractionOnSafari(animationFrameTime: number, nextTaskTime: number) {
  // On Safari, we have a bug where the animation is not being called when it should and we report
  // high latencies, even when the event loop isn't busy. In that case, we want to try to detect that
  // and discard the event.

  if (!isSafari()) {
    // The current browser is not impacted.
    return false;
  }

  const animationFrameDelayGracePeriod = 30; // ms.

  // The animation frame was scheduled before the next task, so we expect it to be called before the next
  // task. However, we'll give it a 30ms grace period just to handle the case when another animation frame
  // had just been called before we scheduled this animation frame. If this animation frame was called over
  // 30ms after the next task, that means there's some issue with animation frames and we should discard the
  // task.

  // It is important to consider that this method may discard a legitimate redux interaction in the following
  // scenario:
  //  - We call rAF (Frame 1).
  //  - The callback for frame 1 is executed at time T0.
  //  - A redux action occurs and we want to instrument it.
  //  - We call rAF (Frame 2) in measureReduxRender.
  //  - We schedule setTimeout (Task A) in measureReduxRender.
  //  - Another task (Task B) is scheduled somewhere else in the code.
  //  - We cannot yet call the frame 2 callback since 16ms haven't elapsed since the last frame.
  //  - We process task A at time T1, which quickly completes at T2.
  //  - We still cannot call the frame 2 callback since 16ms haven't elapsed.
  //  - We process task B at time T3, which takes a while and ends at T100.
  //  - We execute the frame 2 callback at time T101 since 16ms have elapsed.
  //  - The animation frame time is T101, which is much higher than T1 + 30, so we discard the event.

  return animationFrameTime > nextTaskTime + animationFrameDelayGracePeriod;
}
